import Phaser from 'phaser';
import { getState, subscribe } from '../store.js';
import { tick, ratchet, whooshStart, whooshStop, chime } from '../audio.js';
import { ROYAL_FERRIS_THEME as THEME } from './theme.js';
import { isInFlutter } from '../bridge.js';

function isFlutterSurface() {
  if (isInFlutter) return true;
  try { return /[?&]platform=flutter\b/.test(window.location.search); } catch (_) { return false; }
}

/**
 * GameScene — CTLive royal Ferris wheel.
 *
 * Visual layout (from screenshot reference):
 *  - Antique-gold A-frame base supporting the wheel.
 *  - 8 circular gondolas around a jeweled central hub.
 *  - Center hub contains a crown marker and the live countdown / status text.
 *  - Hot badge on the highest-multiplier slot.
 *  - Slots the user has bet on get a champagne-gold outer ring.
 *
 * Spin animation:
 *  - Three-phase tween (accelerate → cruise → decelerate to winner).
 *  - Continuous "whoosh" oscillator while spinning.
 *  - Ratchet click each time a gondola passes the top marker.
 *  - Final chime + bouncing winner highlight.
 */
export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
    this.gondolas = [];
    this.wheel = null;
    this.hub = null;
    this.countdownText = null;
    this.statusText = null;
    this.mascotText = null;
    this.isSpinning = false;
    this.idleTween = null;
    this.lastTickedSecond = null;
    this.lastTopIndex = null;
    this.tappedHandler = null;
    this.unsubscribe = null;
    // Chase spotlight (screen-space orbit)
    this.spotlightAngle = -Math.PI / 2;
    this.spotlightSpeedMs = 70;
    this._spotlight = null;
    this._spotlightEvent = null;
    this._spotlightLocked = false;
    this._spotlightPulse = null;
    // Tracking glow that visibly hops from pod to pod during spin and lands
    // on the winning pod. Lives at the SCENE level (not inside a pod
    // container) so it always renders above the wheel art with no layering
    // surprises.
    this._trackingGlow = null;
    this._trackingGlowPulse = null;
    this._lastTopFlashAt = 0;
    this.sparklesGroup = null;
    this._flutterSpinBoost = false;
  }

  create() {
    this.cameras.main.setBackgroundColor(THEME.transparent);
    this.cameras.main.transparent = true;
    this._flutterSpinBoost = isFlutterSurface();
    this.layoutAll();
    // Use ResizeObserver on the parent canvas container instead of a global
    // window resize handler. It coalesces multiple events (Android keyboard,
    // orientation, safe-area changes) into a single re-layout per animation
    // frame and we only re-layout when dimensions actually change.
    this._lastSize = { w: this.scale.width, h: this.scale.height };
    const target = this.game.canvas && this.game.canvas.parentElement
      ? this.game.canvas.parentElement
      : document.body;
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObs = new ResizeObserver(() => {
        const w = window.innerWidth;
        const h = window.innerHeight;
        if (w === this._lastSize.w && h === this._lastSize.h) return;
        this._lastSize = { w, h };
        this.layoutAll();
      });
      this._resizeObs.observe(target);
    } else {
      window.addEventListener('resize', () => this.layoutAll());
    }

    this.unsubscribe = subscribe((state) => this.onStateChange(state));
    this.onStateChange(getState());
    // Spotlight chase is intentionally NOT started here — it runs only during the
    // spinning phase (see spinTo()). Pods stay still during betting.
  }

  /**
   * Phaser scene update loop. Runs every frame.
   * Adds a subtle idle rotation to the wheel during the betting phase so the
   * stage feels alive without distracting the user. Idle motion is paused
   * while spinning (spinTo() owns rotation) or when prefers-reduced-motion is
   * set on the document.
   */
  update(_time, delta) {
    if (!this.wheel || this.isSpinning) return;
    if (this._reducedMotion) return;
    const s = getState();
    if (s.status !== 'betting') return;
    // ~0.06 rad/s → one full revolution per ~105s. Slow, ambient.
    this.wheel.rotation += 0.00006 * (delta || 16.67);
    this.updateGondolaCounterRotation();
  }

  layoutAll() {
    const { width, height } = this.scale;
    // Cache reduced-motion preference for the update loop.
    this._reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    // Bottom inset adapts to the actual rendered HUD bottom panel height
    // (which can change with safe-area, history strip, dynamic type) so the
    // wheel never overlaps the panel on small devices.
    const panelEl = document.querySelector('.bottom-panel');
    const topbarEl = document.querySelector('.topbar');
    const measuredBottom = panelEl ? Math.ceil(panelEl.getBoundingClientRect().height) : 0;
    const measuredTop = topbarEl ? Math.ceil(topbarEl.getBoundingClientRect().height) : 56;
    // `tiny` is the half-height-sheet regime (≈50% on short devices), where the
    // old fixed floors over-reserved space and pushed the wheel into the panel.
    const tiny = height < 460;
    const compact = height < 700 || width < 380;
    const topInset = measuredTop + (tiny ? 2 : compact ? 4 : 10);
    // Reserve only what the (CSS-compacted) bottom panel actually needs.
    const minBottomInset = tiny ? 116 : compact ? 168 : 210;
    const maxBottomInset = tiny ? 200 : 340;
    const bottomInset = Math.max(minBottomInset, Math.min(maxBottomInset, measuredBottom + (compact ? 8 : 16)));
    // Use the REAL remaining height (small floor only). Earlier this floored at
    // 210/280 which could exceed the real space on a short sheet, dropping `cy`
    // too low so the A-frame base overlapped the panel.
    const availH = Math.max(120, height - topInset - bottomInset);
    const cx = width / 2;
    const cy = topInset + availH / 2;
    // Bounding R by availH*0.46 keeps the base (cy + R*1.05) above the panel:
    // cy + R*1.05 ≤ topInset + availH*0.983 < topInset + availH = panel top.
    const R = Math.max(54, Math.min(width * (compact ? 0.40 : 0.42), availH * 0.46));

    // Clear & redraw
    this.children.removeAll();
    this.gondolas = [];

    // ---- A-FRAME BASE (behind wheel) ----
    const baseGfx = this.add.graphics();
    baseGfx.setDepth(-2);
    // Two diagonal poles
    const baseY = cy + R * 1.05;
    const halfBase = R * 0.85;
    const drawPole = (x1, y1, x2, y2) => {
      // Outer pole
      baseGfx.lineStyle(20, THEME.goldDeep, 1);
      baseGfx.beginPath();
      baseGfx.moveTo(x1, y1); baseGfx.lineTo(x2, y2);
      baseGfx.strokePath();
      // Inner highlight (lighter blue diagonal stripes)
      baseGfx.lineStyle(10, THEME.goldBright, 0.9);
      baseGfx.beginPath();
      baseGfx.moveTo(x1, y1); baseGfx.lineTo(x2, y2);
      baseGfx.strokePath();
    };
    drawPole(cx, cy, cx - halfBase, baseY);
    drawPole(cx, cy, cx + halfBase, baseY);
    baseGfx.fillStyle(THEME.antiqueGold, 1);
    baseGfx.fillRoundedRect(cx - halfBase - 18, baseY - 6, halfBase * 2 + 36, 14, 7);
    baseGfx.lineStyle(2, THEME.champagne, 1);
    baseGfx.strokeRoundedRect(cx - halfBase - 18, baseY - 6, halfBase * 2 + 36, 14, 7);

    // Diagonal stripes inside the A-frame triangle (decorative, soft)
    const stripes = this.add.graphics();
    stripes.setDepth(-2);
    stripes.lineStyle(2, THEME.champagne, 0.24);
    for (let s = -6; s <= 6; s++) {
      const ox = s * 28;
      stripes.beginPath();
      stripes.moveTo(cx + ox, cy + 30);
      stripes.lineTo(cx + ox + 60, baseY);
      stripes.strokePath();
    }
    // Mask stripes to A-frame triangle
    const mask = this.make.graphics({}, false);
    mask.fillStyle(THEME.pearl);
    mask.beginPath();
    mask.moveTo(cx, cy);
    mask.lineTo(cx - halfBase, baseY);
    mask.lineTo(cx + halfBase, baseY);
    mask.closePath();
    mask.fillPath();
    stripes.setMask(mask.createGeometryMask());

    // ---- WHEEL DROP SHADOW (behind wheel, non-rotating) ----
    const wheelShadow = this.add.graphics();
    wheelShadow.setDepth(-1);
    wheelShadow.fillStyle(THEME.shadow, 0.24);
    wheelShadow.fillEllipse(cx, cy + R + 18, R * 1.7, 22);

    // ---- WHEEL (rotating root) ----
    this.wheel = this.add.container(cx, cy);
    this.wheel.setDepth(0);

    // Outer glow ring (soft halo behind everything in wheel)
    const glow = this.add.graphics();
    glow.fillStyle(THEME.amethystBright, 0.18);
    glow.fillCircle(0, 0, R + 12);
    glow.fillStyle(THEME.goldBright, 0.12);
    glow.fillCircle(0, 0, R + 4);
    this.wheel.add(glow);

    // Outer ring stroke removed per design — just keep gold rim dots
    const ring = this.add.graphics();
    ring.fillStyle(THEME.goldBright, 1);
    for (let i = 0; i < 36; i++) {
      const a = (i / 36) * Math.PI * 2;
      ring.fillCircle(Math.cos(a) * (R - 6), Math.sin(a) * (R - 6), 2.5);
    }
    this.wheel.add(ring);

    // Spokes (polished royal gold with jewel highlights)
    const spokes = this.add.graphics();
    spokes.lineStyle(8, THEME.goldDeep, 1);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      spokes.beginPath();
      spokes.moveTo(0, 0);
      spokes.lineTo(Math.cos(a) * (R - 14), Math.sin(a) * (R - 14));
      spokes.strokePath();
    }
    spokes.lineStyle(3, THEME.champagne, 0.95);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      spokes.beginPath();
      spokes.moveTo(0, 0);
      spokes.lineTo(Math.cos(a) * (R - 14), Math.sin(a) * (R - 14));
      spokes.strokePath();
    }
    this.wheel.add(spokes);

    // ---- TOP-OF-WHEEL POINTER (fixed indicator above 12 o'clock) ----
    // Hidden during idle/betting; shown during spinning + result so the user
    // can clearly see which pod is being ticked past and which one lands.
    // Sits *above* the gold rim so it never overlaps the wheel artwork.
    const podRForMarker = Math.min(R * 0.22, 46);
    this._ensureRoyalSpinBoostTextures();
    const markerY = cy - R - 16; // above the rim, not on top of pods

    const markerGroup = this.add.container(cx, markerY);
    markerGroup.setDepth(4);
    markerGroup.setVisible(false);

    // Drop shadow disc
    const mShadow = this.add.graphics();
    mShadow.fillStyle(THEME.shadow, 0.38);
    mShadow.fillCircle(0, 4, 22);
    markerGroup.add(mShadow);

    // Outer gold ring
    const mRing = this.add.graphics();
    mRing.fillStyle(THEME.goldBright, 1);
    mRing.fillCircle(0, 0, 22);
    mRing.fillStyle(THEME.plumDeep, 1);
    mRing.fillCircle(0, 0, 18);
    mRing.lineStyle(2, THEME.champagneSoft, 1);
    mRing.strokeCircle(0, 0, 18);
    markerGroup.add(mRing);

    // Down-pointing chevron
    const chevron = this.add.graphics();
    chevron.fillStyle(THEME.champagneSoft, 1);
    chevron.beginPath();
    chevron.moveTo(-9, -3);
    chevron.lineTo(9, -3);
    chevron.lineTo(0, 9);
    chevron.closePath();
    chevron.fillPath();
    markerGroup.add(chevron);

    this._topMarker = markerGroup;

    // Fixed selector ring sitting at 12 o'clock (in screen space). Pods rotate
    // through it during spin. Far more legible than an orbiting halo because
    // the user always knows where to look.
    const selectorRing = this.add.graphics();
    selectorRing.setDepth(1.6); // above wheel/pods (wheel container=0), below hub (2)
    selectorRing.lineStyle(6, THEME.goldBright, 1);
    selectorRing.strokeCircle(0, 0, podRForMarker + 6);
    selectorRing.lineStyle(2, THEME.pearl, 0.9);
    selectorRing.strokeCircle(0, 0, podRForMarker + 10);
    selectorRing.setPosition(cx, cy - R + podRForMarker + 6);
    selectorRing.setVisible(false);
    this._selectorRing = selectorRing;

    // (Top marker indicator removed per design: no down-arrow / circle border.)

    // ---- HUB (jeweled royal medallion) — non-rotating, on top ----
    const hubR = Math.min(78, R * 0.36);
    const hubGfx = this.add.graphics();
    hubGfx.setDepth(2);
    // soft halo
    hubGfx.fillStyle(THEME.amethystBright, 0.3);
    hubGfx.fillCircle(cx, cy, hubR + 8);
    hubGfx.fillStyle(THEME.plumBlack, 1);
    hubGfx.fillCircle(cx, cy, hubR);
    hubGfx.fillStyle(THEME.plum, 1);
    hubGfx.fillCircle(cx, cy - 2, hubR - 2);
    hubGfx.fillStyle(THEME.sapphire, 0.58);
    hubGfx.fillCircle(cx, cy - hubR * 0.15, hubR - 6);
    hubGfx.lineStyle(5, THEME.goldBright, 1);
    hubGfx.strokeCircle(cx, cy, hubR);
    hubGfx.lineStyle(1.5, THEME.champagneSoft, 0.9);
    hubGfx.strokeCircle(cx, cy, hubR - 4);
    // golden bulbs around hub
    hubGfx.fillStyle(THEME.goldBright, 1);
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2;
      const bx = cx + Math.cos(a) * (hubR - 5);
      const by = cy + Math.sin(a) * (hubR - 5);
      hubGfx.fillCircle(bx, by, 3);
    }
    // small inner highlight
    hubGfx.fillStyle(THEME.pearl, 0.28);
    hubGfx.fillEllipse(cx, cy - hubR * 0.45, hubR * 1.1, hubR * 0.35);
    this.hub = hubGfx;

    this.mascotText = this.add.text(cx, cy - hubR * 0.32, '👑', {
      fontFamily: 'sans-serif',
      fontSize: `${Math.round(hubR * 0.55)}px`,
    }).setOrigin(0.5).setDepth(3);

    // Orbiting diamond around mascot — a small cue that this is the hub.
    if (!this._reducedMotion) {
      const orbit = this.add.text(cx, cy - hubR * 0.32, '💎', {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(hubR * 0.22)}px`,
      }).setOrigin(0.5).setDepth(3).setAlpha(0.9);
      const orbitR = hubR * 0.45;
      const orbitCenter = { x: cx, y: cy - hubR * 0.32 };
      this.tweens.addCounter({
        from: 0, to: Math.PI * 2,
        duration: 4200, repeat: -1, ease: 'Linear',
        onUpdate: (tw) => {
          const a = tw.getValue();
          orbit.setPosition(orbitCenter.x + Math.cos(a) * orbitR, orbitCenter.y + Math.sin(a) * orbitR * 0.55);
        },
      });
      this._mascotOrbit = orbit;
    }

    // Countdown / status (below mascot, inside hub)
    this.statusText = this.add.text(cx, cy + hubR * 0.18, 'Get ready', {
      fontFamily: 'sans-serif',
      fontSize: '11px',
      color: THEME.textIvory,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(3);

    // Larger, dominant countdown number (the audit called this out as buried).
    this.countdownText = this.add.text(cx, cy + hubR * 0.55, '—', {
      fontFamily: 'sans-serif',
      fontSize: `${Math.round(hubR * 0.7)}px`,
      fontStyle: 'bold',
      color: THEME.textIvory,
    }).setOrigin(0.5).setDepth(3);

    this.wheelRadius = R;
    this.wheelCenter = { x: cx, y: cy };
    this.hubRadius = hubR;

    this.rebuildGondolasFromState();
    // Pods/wheel stay still during betting; idle rotation is disabled per design.
  }

  // ---------- ROTATION ----------
  // Idle rotation removed by design — wheel only rotates during a spin.
  startIdleRotation() { /* no-op: pods stay still while betting */ }
  stopIdleRotation() {
    if (this.idleTween) { this.idleTween.stop(); this.idleTween = null; }
  }

  // ---------- GONDOLAS ----------
  rebuildGondolasFromState() {
    const { config, myBets } = getState();
    const slots = (config && config.slots) || [];
    if (!slots.length) return;
    this.gondolas.forEach((g) => g.destroy());
    this.gondolas = [];

    const R = this.wheelRadius;
    const N = slots.length;
    const podR = Math.min(R * 0.22, 46);

    // Find the "hot" slot (highest multiplier)
    let hottest = slots[0];
    slots.forEach((s) => { if ((s.multiplier || 0) > (hottest.multiplier || 0)) hottest = s; });

    slots.forEach((slot, i) => {
      const angle = -Math.PI / 2 + (i / N) * Math.PI * 2;
      const r = R - podR - 6;
      const x = Math.cos(angle) * r;
      const y = Math.sin(angle) * r;
      const container = this.add.container(x, y);

      // Pod soft drop shadow
      const podShadow = this.add.graphics();
      podShadow.fillStyle(THEME.shadow, 0.26);
      podShadow.fillCircle(0, 4, podR + 1);
      container.add(podShadow);

      // Pod base: ivory carriage disc with polished gold trim.
      const ringGfx = this.add.graphics();
      ringGfx.fillStyle(THEME.podShell, 1);
      ringGfx.fillCircle(0, 0, podR);
      ringGfx.lineStyle(3, THEME.goldBright, 1);
      ringGfx.strokeCircle(0, 0, podR - 1);
      ringGfx.lineStyle(1, THEME.champagneSoft, 0.75);
      ringGfx.strokeCircle(0, 0, podR - 4);
      container.add(ringGfx);

      // Inner champagne lower half (the seat where the food sits)
      const seatGfx = this.add.graphics();
      seatGfx.fillStyle(THEME.podSeat, 1);
      seatGfx.beginPath();
      seatGfx.arc(0, 0, podR - 4, 0, Math.PI, false);
      seatGfx.closePath();
      seatGfx.fillPath();
      // top white highlight gloss
      seatGfx.fillStyle(THEME.podSeatGlow, 0.72);
      seatGfx.fillEllipse(0, -podR * 0.4, podR * 1.2, podR * 0.45);
      container.add(seatGfx);

      // Continuous spin-phase halo sits behind the emoji/multiplier so the
      // pod looks energized without obscuring the art.
      const spinHalo = this.add.graphics();
      this._paintPodSpinHalo(spinHalo, podR, THEME.glowRamp.fast, 0.4);
      spinHalo.setVisible(false);
      spinHalo.setAlpha(0);
      container.add(spinHalo);

      // Food emoji (upper half)
      const emoji = this.add.text(0, -podR * 0.32, slot.emoji || '🎁', {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(podR * 0.95)}px`,
      }).setOrigin(0.5);
      container.add(emoji);

      // Multiplier label stays dark for contrast against the champagne seat.
      const isHottest = slot.id === hottest.id;
      const winLabel = this.add.text(0, podR * 0.45, `×${slot.multiplier}`, {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(podR * 0.46)}px`,
        fontStyle: '900',
        color: isHottest ? THEME.textBronze : THEME.textPlum,
      }).setOrigin(0.5);
      winLabel.setShadow(0, 1, 'rgba(255,255,255,0.72)', 1, false, true);
      container.add(winLabel);

      // Decorative coin(s) at bottom rim
      const coin = this.add.text(0, podR * 0.85, '🪙', {
        fontFamily: 'sans-serif', fontSize: `${Math.round(podR * 0.3)}px`,
      }).setOrigin(0.5);
      container.add(coin);

      // "Hot" badge for highest-multiplier slot — a jeweled gold flame chip.
      if (slot.id === hottest.id) {
        const hotW = podR * 1.05;
        const hotH = 22;
        const hotBg = this.add.graphics();
        hotBg.fillStyle(THEME.goldBright, 1);
        hotBg.fillRoundedRect(-hotW / 2, -podR - hotH - 2, hotW, hotH, 11);
        hotBg.lineStyle(2, THEME.ruby, 1);
        hotBg.strokeRoundedRect(-hotW / 2, -podR - hotH - 2, hotW, hotH, 11);
        const hotText = this.add.text(0, -podR - hotH / 2 - 2, '🔥 HOT', {
          fontFamily: 'sans-serif',
          fontSize: '12px',
          fontStyle: '900',
          color: THEME.textRuby,
        }).setOrigin(0.5);
        container.add(hotBg);
        container.add(hotText);
        // Subtle aura pulse so the eye is drawn to the hottest pod.
        if (!this._reducedMotion) {
          const aura = this.add.graphics();
          aura.lineStyle(3, THEME.amethystBright, 0.55);
          aura.strokeCircle(0, 0, podR + 6);
          container.addAt(aura, 0);
          this.tweens.add({
            targets: aura,
            alpha: { from: 0.6, to: 0.18 },
            scale: { from: 1, to: 1.08 },
            yoyo: true,
            repeat: -1,
            duration: 950,
            ease: 'Sine.InOut',
          });
        }
      }

      // Selected ring — glowing gold dashed outline shown when the user has
      // a bet on this slot. Less aggressive than a full red ring on every
      // betted pod, and harmonises with the hottest-pod aura.
      const selRing = this.add.graphics();
      selRing.lineStyle(3, THEME.selectedRing, 1);
      selRing.strokeCircle(0, 0, podR + 4);
      selRing.lineStyle(1, THEME.selectedRingInner, 0.9);
      selRing.strokeCircle(0, 0, podR + 7);
      selRing.setVisible(false);
      container.add(selRing);

      // Spin-phase lightning lives inside each pod, above the fruit/multiplier
      // art. This makes the active tick visible on every device even if the
      // scene-level glow is clipped, blurred, or rendered under WebView quirks.
      const spinGlow = this.add.graphics();
      this._paintPodLightning(spinGlow, podR, THEME.glowRamp.fast, 1);
      spinGlow.setVisible(false);
      spinGlow.setAlpha(0);
      container.add(spinGlow);

      let flutterSpinAura = null;
      let flutterSpinCrest = null;
      let flutterSpinBaseScale = 1;
      if (this._flutterSpinBoost && this.textures.exists('royalSpinBoostAura')) {
        flutterSpinBaseScale = (podR * 2.35) / 160;
        flutterSpinAura = this.add.image(0, 0, 'royalSpinBoostAura')
          .setVisible(false)
          .setAlpha(0)
          .setScale(flutterSpinBaseScale);
        flutterSpinAura.setBlendMode(Phaser.BlendModes.NORMAL);
        container.add(flutterSpinAura);
      }

      // Device-stable overlay: orbit dots + bolt accent. Text/circle objects
      // remain visible even where Graphics-based glow can read too softly.
      const spinFx = this.add.container(0, 0);
      spinFx.setVisible(false);
      spinFx.setAlpha(0);
      const spinDotOffsets = [
        { x: 0, y: -(podR * 0.72) },
        { x: podR * 0.72, y: 0 },
        { x: 0, y: podR * 0.72 },
        { x: -(podR * 0.72), y: 0 },
      ];
      const spinDots = spinDotOffsets.map((offset, idx2) => {
        const dot = this.add.circle(offset.x, offset.y, 6.5, idx2 % 2 === 0 ? THEME.sapphireGlow : THEME.goldBright, 1);
        dot.setAlpha(0.9);
        spinFx.add(dot);
        return dot;
      });
      const spinBolt = this.add.text(0, -podR * 0.52, '⚡', {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(podR * 0.72)}px`,
        fontStyle: '900',
        color: THEME.textIvory,
      }).setOrigin(0.5);
      spinBolt.setVisible(false);
      spinBolt.setShadow(0, 0, 'rgba(255,125,163,0.95)', 10, false, true);
      spinFx.add(spinBolt);
      container.add(spinFx);

      if (this._flutterSpinBoost && this.textures.exists('royalSpinBoostCrest')) {
        flutterSpinCrest = this.add.image(0, 0, 'royalSpinBoostCrest')
          .setVisible(false)
          .setAlpha(0)
          .setScale(flutterSpinBaseScale);
        flutterSpinCrest.setBlendMode(Phaser.BlendModes.NORMAL);
        container.add(flutterSpinCrest);
      }

      // Bet badge with current amount
      const badgeBg = this.add.graphics();
      badgeBg.fillStyle(THEME.badgeFill, 1);
      badgeBg.fillCircle(podR * 0.7, -podR * 0.7, 14);
      badgeBg.lineStyle(2, THEME.badgeStroke, 1);
      badgeBg.strokeCircle(podR * 0.7, -podR * 0.7, 14);
      const badgeText = this.add.text(podR * 0.7, -podR * 0.7, '', {
        fontFamily: 'sans-serif', fontSize: '10px', fontStyle: 'bold', color: THEME.textIvory,
      }).setOrigin(0.5);
      badgeBg.setVisible(false);
      badgeText.setVisible(false);
      container.add(badgeBg);
      container.add(badgeText);

      // Tap interactivity
      container.setSize(podR * 2 + 8, podR * 2 + 8);
      container.setInteractive({ useHandCursor: true });
      container.on('pointerdown', () => this.handleSlotTap(slot.id));

      container.__slot = slot;
      container.__emoji = emoji;
      container.__coin = coin;
      container.__multLabel = winLabel;
      container.__multBaseColor = isHottest ? THEME.textBronze : THEME.textPlum;
      container.__selRing = selRing;
      container.__badgeBg = badgeBg;
      container.__badgeText = badgeText;
      container.__spinHalo = spinHalo;
      container.__spinGlow = spinGlow;
      container.__spinFx = spinFx;
      container.__spinDots = spinDots;
      container.__spinBolt = spinBolt;
      container.__flutterSpinAura = flutterSpinAura;
      container.__flutterSpinCrest = flutterSpinCrest;
      container.__flutterSpinBaseScale = flutterSpinBaseScale;
      container.__spinPulseUntil = 0;
      container.__angleIdx = i;

      this.wheel.add(container);
      this.gondolas.push(container);
    });

    this.updateGondolaCounterRotation();
    this.updateBadges(myBets || {});
  }

  updateGondolaCounterRotation() {
    if (!this.wheel) return;
    const wr = this.wheel.rotation;
    this.gondolas.forEach((g) => { g.rotation = -wr; });

    if (this.isSpinning) this._updateSpinPhaseEffects();

    // Emit ratchet ticks while spinning when a new gondola crosses the top.
    // Also briefly scale the new top pod so users see the selector ticking.
    if (this.isSpinning && this.gondolas.length > 0) {
      const N = this.gondolas.length;
      // Index whose center is at top: i such that wr + (-PI/2 + i*2PI/N) ≡ -PI/2  mod 2PI
      // → i ≈ (-wr) / (2PI/N) mod N
      let raw = (-wr) / (Math.PI * 2 / N);
      raw = ((raw % N) + N) % N;
      const idx = Math.floor(raw);
      if (idx !== this.lastTopIndex) {
        this.lastTopIndex = idx;
        ratchet();
        this._flashTopPod(idx);
      }
    }
  }

  // ---------- SELECTOR (fixed 12 o'clock highlight) ----------
  // The audit found that the previous orbiting halo was visually invisible
  // (rendered behind pods) so users had no spin-phase feedback. The new
  // approach is a *fixed* selector ring at the top of the wheel — pods
  // rotate through it during the spin, the marker chevron points down at it,
  // and each pod that crosses the top gets a brief scale + glow flash. This
  // mirrors the mental model of a roulette/wheel-of-fortune ticker.
  _ensureSpotlightSprite() {
    if (this._spotlight) return;
    const g = this.add.graphics();
    // Bright, contrasting halo behind the selector ring (sits in front of the
    // wheel container so it's actually visible, but behind the hub).
    g.fillStyle(THEME.goldBright, 0.5);
    g.fillCircle(0, 0, 64);
    g.fillStyle(THEME.amethystBright, 0.62);
    g.fillCircle(0, 0, 44);
    g.setDepth(1.4);
    g.setVisible(false);
    this._spotlight = g;
  }

  _positionSpotlight() {
    if (!this._spotlight || !this.wheelCenter) return;
    const { x: cx, y: cy } = this.wheelCenter;
    const podR = Math.min(this.wheelRadius * 0.22, 46);
    // Always at the top of the wheel (where pods land at the end of a spin).
    this._spotlight.setPosition(cx, cy - this.wheelRadius + podR + 6);
    const s = (podR + 14) / 56;
    this._spotlight.setScale(s);
  }

  _scheduleSpotlight() {
    // No-op: the new selector is fixed at the top, no orbit timer needed.
  }

  _stopSpotlight() {
    if (this._spotlight) {
      this.tweens.killTweensOf(this._spotlight);
      this._spotlight.setVisible(false);
      this._spotlight.alpha = 1;
      this._spotlight.setScale(1);
    }
    if (this._selectorRing) {
      this.tweens.killTweensOf(this._selectorRing);
      this._selectorRing.setVisible(false);
      this._selectorRing.alpha = 1;
    }
    if (this._topMarker) {
      this.tweens.killTweensOf(this._topMarker);
      this._topMarker.setVisible(false);
      this._topMarker.alpha = 1;
      this._topMarker.setScale(1);
    }
    this._spotlightLocked = false;
  }

  _setSpotlightSpeed(_ms) {
    // Retained as a no-op so spinTo()'s decel-phase calls keep working.
  }

  _startSpotlightChase() {
    this._ensureSpotlightSprite();
    this._spotlight.setVisible(true);
    this._spotlight.alpha = 1;
    this._positionSpotlight();
    if (this._selectorRing) {
      this._selectorRing.setVisible(true);
      this._selectorRing.alpha = 1;
    }
    if (this._topMarker) {
      this._topMarker.setVisible(true);
      this._topMarker.alpha = 1;
    }
    // Pulse the halo + selector ring + chevron together so the spin phase is
    // unmistakable even on a static screenshot.
    this._spotlightPulse = this.tweens.add({
      targets: this._spotlight,
      alpha: { from: 1, to: 0.6 },
      scale: { from: this._spotlight.scaleX, to: this._spotlight.scaleX * 1.08 },
      yoyo: true,
      repeat: -1,
      duration: 320,
      ease: 'Sine.InOut',
    });
    if (this._selectorRing) {
      this.tweens.add({
        targets: this._selectorRing,
        alpha: { from: 1, to: 0.55 },
        yoyo: true,
        repeat: -1,
        duration: 320,
        ease: 'Sine.InOut',
      });
    }
    if (this._topMarker) {
      this.tweens.add({
        targets: this._topMarker,
        scale: { from: 1, to: 1.18 },
        yoyo: true,
        repeat: -1,
        duration: 280,
        ease: 'Sine.InOut',
      });
    }
    this._spotlightLocked = false;
  }

  _lockSpotlightOn(_winningIdx) {
    this._spotlightLocked = true;
    this._ensureSpotlightSprite();
    this._spotlight.setVisible(true);
    this._positionSpotlight();
    if (this._spotlightPulse) { this._spotlightPulse.stop(); this._spotlightPulse = null; }
    if (this._selectorRing) {
      this.tweens.killTweensOf(this._selectorRing);
      this._selectorRing.setVisible(true);
      this._selectorRing.alpha = 1;
    }
    if (this._topMarker) {
      this.tweens.killTweensOf(this._topMarker);
      this._topMarker.setVisible(true);
      this._topMarker.setScale(1);
      this.tweens.add({
        targets: this._topMarker,
        scale: { from: 1, to: 1.25 },
        yoyo: true,
        repeat: -1,
        duration: 600,
        ease: 'Sine.InOut',
      });
    }
    this.tweens.add({
      targets: this._spotlight,
      scale: { from: 1, to: 1.18 },
      yoyo: true,
      repeat: -1,
      duration: 520,
      ease: 'Sine.InOut',
    });
  }

  _unlockSpotlight() {
    this._spotlightLocked = false;
    if (this._spotlight) {
      this.tweens.killTweensOf(this._spotlight);
      this._spotlight.alpha = 1;
      this._positionSpotlight();
    }
  }

  /**
   * Lazily create (or recreate after a layoutAll() teardown) the scene-level
   * tracking-glow sprite. Drawn ABOVE the wheel container with a high depth
   * so it can never be hidden by pod art, spokes, or the hub.
   */
  _ensureTrackingGlow() {
    if (this._trackingGlow && this._trackingGlow.scene) return this._trackingGlow;
    const g = this.add.graphics();
    g.setDepth(2.5); // wheel is depth 0, hub depth 2, marker depth 4
    g.setAlpha(0);
    this._trackingGlow = g;
    return g;
  }

  _paintTrackingGlow(color, podR) {
    const g = this._trackingGlow;
    if (!g) return;
    g.clear();
    // Outermost soft halo
    g.fillStyle(color, 0.18);
    g.fillCircle(0, 0, podR + 28);
    // Mid halo
    g.fillStyle(color, 0.32);
    g.fillCircle(0, 0, podR + 18);
    // Bright thick ring (the visible "spotlight")
    g.lineStyle(8, color, 1);
    g.strokeCircle(0, 0, podR + 10);
    // Crisp white inner highlight for contrast against pod edge
    g.lineStyle(3, THEME.pearl, 0.95);
    g.strokeCircle(0, 0, podR + 4);
  }

  _ensureRoyalSpinBoostTextures() {
    if (this.textures.exists('royalSpinBoostAura') && this.textures.exists('royalSpinBoostCrest')) return;

    if (!this.textures.exists('royalSpinBoostAura')) {
      const aura = this.make.graphics({ x: 0, y: 0 }, false);
      const cx = 80;
      const cy = 80;
      aura.fillStyle(THEME.sapphireGlow, 0.18);
      aura.fillCircle(cx, cy, 74);
      aura.fillStyle(THEME.amethystBright, 0.16);
      aura.fillCircle(cx, cy, 62);
      aura.lineStyle(12, THEME.sapphireGlow, 0.88);
      aura.strokeCircle(cx, cy, 58);
      aura.lineStyle(7, THEME.goldBright, 1);
      aura.strokeCircle(cx, cy, 48);
      aura.lineStyle(3, THEME.pearl, 0.95);
      aura.strokeCircle(cx, cy, 38);
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2;
        const r = i % 2 === 0 ? 66 : 53;
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r;
        aura.fillStyle(i % 2 === 0 ? THEME.goldBright : THEME.pearl, 0.94);
        aura.fillCircle(x, y, i % 2 === 0 ? 4.2 : 3.1);
      }
      aura.generateTexture('royalSpinBoostAura', 160, 160);
      aura.destroy();
    }

    if (!this.textures.exists('royalSpinBoostCrest')) {
      const crest = this.make.graphics({ x: 0, y: 0 }, false);
      const cx = 80;
      const cy = 80;
      crest.lineStyle(10, THEME.goldBright, 1);
      crest.beginPath();
      crest.moveTo(22, 58);
      crest.lineTo(64, 66);
      crest.lineTo(45, 94);
      crest.lineTo(86, 81);
      crest.lineTo(72, 116);
      crest.lineTo(130, 74);
      crest.strokePath();

      crest.lineStyle(5, THEME.pearl, 0.96);
      crest.beginPath();
      crest.moveTo(28, 51);
      crest.lineTo(66, 58);
      crest.lineTo(51, 84);
      crest.lineTo(93, 72);
      crest.lineTo(79, 105);
      crest.lineTo(127, 69);
      crest.strokePath();

      crest.lineStyle(7, THEME.sapphireGlow, 0.9);
      crest.beginPath();
      crest.moveTo(117, 38);
      crest.lineTo(92, 67);
      crest.lineTo(122, 65);
      crest.lineTo(80, 122);
      crest.strokePath();

      crest.fillStyle(THEME.goldBright, 0.98);
      crest.fillCircle(cx, cy - 54, 7);
      crest.fillCircle(cx + 52, cy, 6);
      crest.fillCircle(cx, cy + 54, 6);
      crest.fillCircle(cx - 52, cy, 6);
      crest.fillStyle(THEME.pearl, 0.98);
      crest.fillCircle(cx, cy, 6);
      crest.lineStyle(5, THEME.goldBright, 0.95);
      crest.strokeCircle(cx, cy, 68);
      crest.lineStyle(2, THEME.pearl, 0.9);
      crest.strokeCircle(cx, cy, 72);
      crest.generateTexture('royalSpinBoostCrest', 160, 160);
      crest.destroy();
    }
  }

  _paintPodSpinHalo(g, podR, color, intensity = 1) {
    if (!g) return;
    g.clear();
    const auraAlpha = 0.14 + intensity * 0.2;
    const outerR = podR + 12 + intensity * 10;
    g.fillStyle(color, auraAlpha);
    g.fillCircle(0, 0, outerR);
    g.lineStyle(6 + intensity * 3, color, 0.38 + intensity * 0.46);
    g.strokeCircle(0, 0, podR + 9 + intensity * 6);
    g.lineStyle(3, THEME.pearl, 0.44 + intensity * 0.44);
    g.strokeCircle(0, 0, podR + 14 + intensity * 6);
  }

  _paintPodLightning(g, podR, color, intensity = 1) {
    if (!g) return;
    g.clear();
    g.lineStyle(8 + intensity * 3, color, 0.98);
    g.strokeCircle(0, 0, podR + 10 + intensity * 5);
    g.lineStyle(4, THEME.pearl, 0.9);
    g.strokeCircle(0, 0, podR + 16 + intensity * 5);

    g.lineStyle(5 + intensity * 2.2, THEME.pearl, 1);
    g.beginPath();
    g.moveTo(-podR * 0.88, -podR * 0.28);
    g.lineTo(-podR * 0.22, -podR * 0.14);
    g.lineTo(-podR * 0.52, podR * 0.28);
    g.lineTo(podR * 0.22, podR * 0.06);
    g.lineTo(podR * 0.02, podR * 0.54);
    g.lineTo(podR * 0.9, podR * 0.18);
    g.strokePath();

    g.beginPath();
    g.moveTo(podR * 0.66, -podR * 0.5);
    g.lineTo(podR * 0.24, -podR * 0.1);
    g.lineTo(podR * 0.56, -podR * 0.04);
    g.lineTo(podR * 0.06, podR * 0.48);
    g.strokePath();

    g.lineStyle(3 + intensity * 1.2, THEME.goldBright, 1);
    g.beginPath();
    g.moveTo(-podR * 0.1, -podR * 0.92);
    g.lineTo(0, -podR * 0.55);
    g.lineTo(podR * 0.15, -podR * 0.78);
    g.strokePath();

    g.fillStyle(THEME.pearl, 0.65 + intensity * 0.2);
    g.fillCircle(-podR * 0.78, -podR * 0.16, 1.5 + intensity * 1.8);
    g.fillCircle(podR * 0.72, podR * 0.16, 1.5 + intensity * 1.5);
    g.fillCircle(0, -podR * 0.95, 1.8 + intensity * 2.2);
    g.fillStyle(color, 0.95);
    g.fillCircle(-podR * 0.94, 0, 2.2 + intensity * 2.2);
    g.fillCircle(podR * 0.94, 0, 2.2 + intensity * 2.2);
    g.fillCircle(0, podR * 0.96, 2 + intensity * 2.2);
  }

  _resetPodSpinPresentation(pod) {
    if (!pod) return;
    if (pod.__multLabel) {
      pod.__multLabel.setScale(1);
      pod.__multLabel.setColor(pod.__multBaseColor || THEME.textPlum);
      pod.__multLabel.setShadow(0, 1, 'rgba(255,255,255,0.72)', 1, false, true);
    }
    if (pod.__emoji) {
      pod.__emoji.setScale(1);
      pod.__emoji.setAlpha(1);
    }
    if (pod.__coin) {
      pod.__coin.setScale(1);
      pod.__coin.setAlpha(1);
    }
  }

  _clearPodLightning() {
    if (!this.gondolas) return;
    for (const pod of this.gondolas) {
      pod.__spinPulseUntil = 0;
      if (pod.__spinHalo) {
        this.tweens.killTweensOf(pod.__spinHalo);
        pod.__spinHalo.setVisible(false);
        pod.__spinHalo.setAlpha(0);
        pod.__spinHalo.setScale(1);
      }
      if (!pod.__spinGlow) continue;
      this.tweens.killTweensOf(pod.__spinGlow);
      pod.__spinGlow.setVisible(false);
      pod.__spinGlow.setAlpha(0);
      pod.__spinGlow.setScale(1);
      pod.__spinGlow.setRotation(0);
      if (pod.__spinFx) {
        pod.__spinFx.setVisible(false);
        pod.__spinFx.setAlpha(0);
        pod.__spinFx.setScale(1);
        pod.__spinFx.setRotation(0);
      }
      if (pod.__flutterSpinAura) {
        pod.__flutterSpinAura.setVisible(false);
        pod.__flutterSpinAura.setAlpha(0);
        pod.__flutterSpinAura.setScale(pod.__flutterSpinBaseScale || 1);
        pod.__flutterSpinAura.setRotation(0);
      }
      if (pod.__flutterSpinCrest) {
        pod.__flutterSpinCrest.setVisible(false);
        pod.__flutterSpinCrest.setAlpha(0);
        pod.__flutterSpinCrest.setScale(pod.__flutterSpinBaseScale || 1);
        pod.__flutterSpinCrest.setRotation(0);
      }
      if (pod.__spinBolt) {
        pod.__spinBolt.setVisible(false);
        pod.__spinBolt.setAlpha(0);
        pod.__spinBolt.setScale(1);
      }
      if (pod.__spinDots) {
        for (const dot of pod.__spinDots) {
          dot.setAlpha(0.9);
          dot.setScale(1);
        }
      }
      this._resetPodSpinPresentation(pod);
    }
  }

  _updateSpinPhaseEffects() {
    if (!this.gondolas || this.gondolas.length === 0 || !this.wheel) return;
    const N = this.gondolas.length;
    const podR = Math.min(this.wheelRadius * 0.22, 46);
    let raw = (-this.wheel.rotation) / (Math.PI * 2 / N);
    raw = ((raw % N) + N) % N;
    const now = this.time.now;

    for (const pod of this.gondolas) {
      const idx = Number(pod.__angleIdx || 0);
      let diff = Math.abs(idx - raw);
      diff = Math.min(diff, N - diff);
      const emphasis = Math.max(0, 1 - diff / 2.45);
      const intensity = 0.12 + emphasis * 0.88;

      if (intensity <= 0.03) {
        if (pod.__spinHalo) {
          pod.__spinHalo.setVisible(false);
          pod.__spinHalo.setAlpha(0);
          pod.__spinHalo.setScale(1);
        }
        if (pod.__spinGlow) {
          pod.__spinGlow.setVisible(false);
          pod.__spinGlow.setAlpha(0);
          pod.__spinGlow.setScale(1);
          pod.__spinGlow.setRotation(0);
        }
        if (pod.__spinFx) {
          pod.__spinFx.setVisible(false);
          pod.__spinFx.setAlpha(0);
          pod.__spinFx.setScale(1);
          pod.__spinFx.setRotation(0);
        }
        if (pod.__flutterSpinAura) {
          pod.__flutterSpinAura.setVisible(false);
          pod.__flutterSpinAura.setAlpha(0);
          pod.__flutterSpinAura.setScale(pod.__flutterSpinBaseScale || 1);
          pod.__flutterSpinAura.setRotation(0);
        }
        if (pod.__flutterSpinCrest) {
          pod.__flutterSpinCrest.setVisible(false);
          pod.__flutterSpinCrest.setAlpha(0);
          pod.__flutterSpinCrest.setScale(pod.__flutterSpinBaseScale || 1);
          pod.__flutterSpinCrest.setRotation(0);
        }
        if (pod.__spinBolt) {
          pod.__spinBolt.setVisible(false);
          pod.__spinBolt.setAlpha(0);
          pod.__spinBolt.setScale(1);
        }
        this._resetPodSpinPresentation(pod);
        continue;
      }

      const color = emphasis > 0.72
        ? THEME.glowRamp.slow
        : (emphasis > 0.42 ? THEME.glowRamp.mid : THEME.sapphireGlow);
      const pulse = 0.5 + 0.5 * Math.sin(now / 105 + idx * 0.7);
      const scaleBoost = 1 + emphasis * 0.14 + pulse * emphasis * 0.08;
      const tickPulse = pod.__spinPulseUntil ? Phaser.Math.Clamp((pod.__spinPulseUntil - now) / 260, 0, 1) : 0;
      const boosted = Math.min(1, emphasis + tickPulse * 0.35);

      if (pod.__spinHalo) {
        this._paintPodSpinHalo(pod.__spinHalo, podR, color, intensity);
        pod.__spinHalo.setVisible(true);
        pod.__spinHalo.setAlpha(0.34 + intensity * 0.6);
        pod.__spinHalo.setScale(0.98 + intensity * 0.16 + pulse * 0.04);
      }
      if (pod.__spinGlow) {
        this._paintPodLightning(pod.__spinGlow, podR, color, intensity);
        pod.__spinGlow.setVisible(true);
        pod.__spinGlow.setAlpha(0.24 + intensity * 0.76);
        pod.__spinGlow.setScale(0.96 + intensity * 0.28 + pulse * 0.06);
        pod.__spinGlow.setRotation(Math.sin(now / 82 + idx * 0.8) * 0.16 * intensity);
      }
      if (pod.__spinFx) {
        pod.__spinFx.setVisible(true);
        pod.__spinFx.setAlpha(0.3 + intensity * 0.7);
        pod.__spinFx.setScale(0.96 + intensity * 0.18 + pulse * 0.04);
        pod.__spinFx.setRotation(now / 620 + idx * 0.18);
      }
      if (pod.__flutterSpinAura) {
        const base = pod.__flutterSpinBaseScale || 1;
        pod.__flutterSpinAura.setVisible(true);
        pod.__flutterSpinAura.setAlpha(0.32 + intensity * 0.4 + boosted * 0.2);
        pod.__flutterSpinAura.setScale(base * (0.94 + intensity * 0.22 + pulse * 0.05 + tickPulse * 0.12));
        pod.__flutterSpinAura.setRotation(-(now / 820) + idx * 0.12);
      }
      if (pod.__flutterSpinCrest) {
        const base = pod.__flutterSpinBaseScale || 1;
        pod.__flutterSpinCrest.setVisible(true);
        pod.__flutterSpinCrest.setAlpha(Math.min(1, 0.18 + boosted * 0.76 + tickPulse * 0.18));
        pod.__flutterSpinCrest.setScale(base * (0.86 + boosted * 0.3 + pulse * 0.04 + tickPulse * 0.18));
        pod.__flutterSpinCrest.setRotation((now / 690) + idx * 0.18);
      }
      if (pod.__spinDots) {
        pod.__spinDots.forEach((dot, dotIdx) => {
          const dotPulse = 0.45 + 0.55 * Math.sin(now / 110 + idx * 0.55 + dotIdx * 1.3);
          dot.setAlpha(0.38 + intensity * 0.5 + dotPulse * 0.12);
          dot.setScale(0.96 + intensity * 0.56 + dotPulse * 0.22);
        });
      }
      if (pod.__spinBolt) {
        const boltVisible = emphasis > 0.2;
        pod.__spinBolt.setVisible(boltVisible);
        pod.__spinBolt.setAlpha(boltVisible ? (0.28 + intensity * 0.72) : 0);
        pod.__spinBolt.setScale(0.98 + intensity * 0.42 + pulse * 0.1);
        pod.__spinBolt.setTint(emphasis > 0.55 ? THEME.goldBright : THEME.sapphireGlow);
      }
      if (pod.__multLabel) {
        pod.__multLabel.setScale(scaleBoost);
        pod.__multLabel.setColor(emphasis > 0.6 ? THEME.textIvory : '#ffdf7a');
        pod.__multLabel.setShadow(0, 0, emphasis > 0.45 ? 'rgba(255,125,163,0.98)' : 'rgba(255,223,122,0.95)', Math.round(10 + intensity * 14), false, true);
      }
      if (pod.__emoji) {
        pod.__emoji.setScale(1 + emphasis * 0.16 + pulse * 0.04);
      }
      if (pod.__coin) {
        pod.__coin.setScale(1 + emphasis * 0.14);
        pod.__coin.setAlpha(Math.min(1, 0.78 + intensity * 0.5));
      }
    }
  }

  /**
   * Tracking-glow flash on the pod that just rotated under the top marker.
   * Called from updateGondolaCounterRotation when lastTopIndex changes.
   *
   * Uses a single SCENE-LEVEL glow sprite (`_trackingGlow`) that is
   * repositioned to the active pod's world coordinates each tick. Because
   * pods orbit by being children of the rotating `this.wheel` container, we
   * resolve world XY via the pod's transform matrix — this gives us a glow
   * that appears to hop crisply from pod to pod around the wheel and lands
   * on the winning pod.
   *
    * Hue ramps sapphire → amethyst → bright gold as ticks slow down, so the
   * decel phase visibly builds anticipation toward the winner.
   */
  _flashTopPod(idx) {
    const pod = this.gondolas[idx];
    if (!pod) return;

    const now = this.time.now;
    const dt = this._lastTopFlashAt ? (now - this._lastTopFlashAt) : 180;
    this._lastTopFlashAt = now;
    pod.__spinPulseUntil = now + Math.min(300, Math.max(180, dt + 70));

    // Hue ramps sapphire → amethyst → bright gold as the wheel decelerates.
    let color;
    if (dt < 110)      color = THEME.glowRamp.fast;
    else if (dt < 220) color = THEME.glowRamp.mid;
    else               color = THEME.glowRamp.slow;

    // Resolve the pod's world position (pods are children of rotating wheel).
    const m = pod.getWorldTransformMatrix();
    const wx = m.tx;
    const wy = m.ty;
    const podR = Math.min(this.wheelRadius * 0.22, 46);

    const g = this._ensureTrackingGlow();
    this._paintTrackingGlow(color, podR);
    g.setPosition(wx, wy);

    // SNAP visible — no fade-in. Pods are at top for ~80–250 ms during cruise,
    // so a fade-in eats the visible window. A scale punch sells the impact.
    this.tweens.killTweensOf(g);
    g.setAlpha(1);
    g.setScale(0.85);
    this.tweens.add({
      targets: g,
      scale: { from: 0.85, to: 1.05 },
      duration: 110,
      ease: 'Cubic.Out',
    });

    if (pod.__spinGlow) {
      this.tweens.killTweensOf(pod.__spinGlow);
      this._paintPodLightning(pod.__spinGlow, podR, color);
      pod.__spinGlow.setVisible(true);
      pod.__spinGlow.setAlpha(1);
      pod.__spinGlow.setScale(0.86);
      pod.__spinGlow.setRotation(-0.18);
      this.tweens.add({
        targets: pod.__spinGlow,
        alpha: { from: 1, to: 0 },
        scale: { from: 0.86, to: 1.2 },
        rotation: { from: -0.18, to: 0.18 },
        duration: Math.min(300, Math.max(150, dt + 40)),
        ease: 'Cubic.Out',
        onComplete: () => {
          if (pod.__spinGlow) pod.__spinGlow.setVisible(false);
        },
      });
    }

    // Pod scale pulse for extra emphasis on the active pod.
    this.tweens.killTweensOf(pod);
    pod.setScale(1);
    this.tweens.add({
      targets: pod,
      scale: { from: 1.18, to: 1 },
      duration: Math.min(260, Math.max(140, dt)),
      ease: 'Cubic.Out',
    });
  }

  // Burst sparkles at given screen position (center of pod)
  _spawnSparkles(centerX, centerY) {
    const colors = THEME.sparkles;
    for (let i = 0; i < 18; i++) {
      const a = Math.random() * Math.PI * 2;
      const dist = 30 + Math.random() * 60;
      const dot = this.add.circle(centerX, centerY, 3 + Math.random() * 3, Phaser.Utils.Array.GetRandom(colors));
      dot.setDepth(5);
      this.tweens.add({
        targets: dot,
        x: centerX + Math.cos(a) * dist,
        y: centerY + Math.sin(a) * dist,
        alpha: { from: 1, to: 0 },
        scale: { from: 1, to: 0.2 },
        duration: 700 + Math.random() * 300,
        ease: 'Cubic.Out',
        onComplete: () => dot.destroy(),
      });
    }
  }

  // ---------- TAPS ----------
  handleSlotTap(slotId) {
    if (this.tappedHandler) this.tappedHandler(slotId);
    const g = this.gondolas.find((x) => x.__slot && x.__slot.id === slotId);
    if (g) {
      this.tweens.add({ targets: g, scale: { from: 1.18, to: 1 }, duration: 220, ease: 'Back.Out' });
    }
  }

  setOnSlotTapped(fn) { this.tappedHandler = fn; }

  // ---------- COUNTDOWN ----------
  pulseCountdown(remainingSec) {
    if (this.lastTickedSecond === remainingSec) return;
    this.lastTickedSecond = remainingSec;
    if (remainingSec <= 0) return;
    if (remainingSec <= 3) tick(true); else tick(false);
    this.tweens.add({
      targets: this.countdownText,
      scale: { from: 1.35, to: 1 },
      duration: 320,
      ease: 'Cubic.Out',
    });
    this.countdownText.setColor(remainingSec <= 3 ? '#ffdf7a' : THEME.textIvory);
  }

  // ---------- BADGES ----------
  updateBadges(myBets) {
    this.gondolas.forEach((g) => {
      const sid = g.__slot && g.__slot.id;
      const amount = myBets[sid] || 0;
      if (amount > 0) {
        g.__badgeBg.setVisible(true);
        g.__badgeText.setVisible(true);
        g.__badgeText.setText(formatShort(amount));
        g.__selRing.setVisible(true);
      } else {
        g.__badgeBg.setVisible(false);
        g.__badgeText.setVisible(false);
        g.__selRing.setVisible(false);
      }
    });
  }

  // ---------- SPIN ----------
  spinTo(winningSlotId, durationMs = 4500) {
    if (this.isSpinning) return Promise.resolve();
    this.isSpinning = true;
    this.lastTopIndex = null;
    this._lastTopFlashAt = 0;
    // Reset any pinned tracking glow from the previous round.
    if (this._trackingGlowPulse) { this._trackingGlowPulse.stop(); this._trackingGlowPulse = null; }
    if (this._trackingGlow) {
      this.tweens.killTweensOf(this._trackingGlow);
      this._trackingGlow.setAlpha(0);
      this._trackingGlow.setScale(1);
    }
    this._clearPodLightning();
    this.stopIdleRotation();
    this._unlockSpotlight();

    const target = this.gondolas.find((g) => g.__slot && g.__slot.id === winningSlotId);
    if (!target) { this.isSpinning = false; return Promise.resolve(); }
    const winningIdx = target.__angleIdx;

    // Start the chase spotlight only at spin time (orbits in screen space).
    this._setSpotlightSpeed(70);
    this._startSpotlightChase();
    this._updateSpinPhaseEffects();

    const N = this.gondolas.length;
    const i = target.__angleIdx;
    const slotAngle = -Math.PI / 2 + (i / N) * Math.PI * 2;
    const targetAngle = -slotAngle - Math.PI / 2;
    const currentR = this.wheel.rotation % (Math.PI * 2);
    let delta = targetAngle - currentR;
    while (delta < 0) delta += Math.PI * 2;
    const totalRevs = 5;
    const finalRot = this.wheel.rotation + delta + Math.PI * 2 * totalRevs;

    // Visual cue: pulse hub red→bright while spinning
    this.tweens.add({
      targets: this.hub,
      alpha: { from: 1, to: 0.85 },
      yoyo: true,
      repeat: Math.floor(durationMs / 220),
      duration: 110,
    });

    // Continuous whoosh + ratchet (ratchet tick handled in updateGondolaCounterRotation)
    whooshStart(durationMs);

    // Three-phase tween: accel (15%) → cruise (35%) → decel (50%)
    const accelMs = Math.round(durationMs * 0.18);
    const cruiseMs = Math.round(durationMs * 0.30);
    const decelMs = durationMs - accelMs - cruiseMs;
    const accelTarget = this.wheel.rotation + (finalRot - this.wheel.rotation) * 0.18;
    const cruiseTarget = this.wheel.rotation + (finalRot - this.wheel.rotation) * 0.55;

    return new Promise((resolve) => {
      this.tweens.add({
        targets: this.wheel,
        rotation: accelTarget,
        duration: accelMs,
        ease: 'Cubic.In',
        onUpdate: () => this.updateGondolaCounterRotation(),
        onComplete: () => {
          this._setSpotlightSpeed(55);
          this.tweens.add({
            targets: this.wheel,
            rotation: cruiseTarget,
            duration: cruiseMs,
            ease: 'Linear',
            onUpdate: () => this.updateGondolaCounterRotation(),
            onComplete: () => {
              // Decel phase: progressively slow the chase spotlight too
              const phaseStart = this.time.now;
              const stepEvent = this.time.addEvent({
                delay: 100,
                loop: true,
                callback: () => {
                  const t = Phaser.Math.Clamp((this.time.now - phaseStart) / decelMs, 0, 1);
                  // ease-out: 55 → 220
                  this._setSpotlightSpeed(Math.round(55 + (220 - 55) * (t * t)));
                },
              });
              this.tweens.add({
                targets: this.wheel,
                rotation: finalRot,
                duration: decelMs,
                ease: 'Cubic.Out',
                onUpdate: () => this.updateGondolaCounterRotation(),
                onComplete: () => {
                  stepEvent.remove();
                  this.isSpinning = false;
                  whooshStop();
                  this._clearPodLightning();

                  // Lock spotlight onto winner immediately
                  this._lockSpotlightOn(winningIdx);

                  // Pin the tracking glow on the winning pod with a strong
                  // royal-gold pulse, so the landed multiplier is unmistakable
                  // through the result phase.
                  {
                    const m = target.getWorldTransformMatrix();
                    const podR = Math.min(this.wheelRadius * 0.22, 46);
                    const wg = this._ensureTrackingGlow();
                    this._paintTrackingGlow(THEME.glowRamp.final, podR);
                    wg.setPosition(m.tx, m.ty);
                    this.tweens.killTweensOf(wg);
                    wg.setAlpha(1);
                    wg.setScale(1);
                    if (!this._reducedMotion) {
                      this._trackingGlowPulse = this.tweens.add({
                        targets: wg,
                        alpha: { from: 1, to: 0.65 },
                        scale: { from: 1, to: 1.12 },
                        yoyo: true,
                        repeat: -1,
                        duration: 620,
                        ease: 'Sine.InOut',
                      });
                    }
                  }

                  // Winner highlight: bouncing scale.
                  // Reserve camera shake for big wins only — when the user has
                  // a bet on the winning slot. A constant per-spin shake fatigues.
                  const myBets = getState().myBets || {};
                  const hasBetOnWinner = (myBets[winningSlotId] || 0) > 0;
                  if (hasBetOnWinner && !this._reducedMotion) {
                    this.cameras.main.shake(220, 0.004);
                  }
                  this.tweens.add({
                    targets: target,
                    scale: { from: 1, to: 1.32 },
                    yoyo: true,
                    repeat: 2,
                    duration: 280,
                    ease: 'Sine.InOut',
                  });
                  // Pulsing royal-gold ring around winner
                  const flash = this.add.graphics();
                  flash.lineStyle(7, THEME.winnerRing, 1);
                  const podR = Math.min(this.wheelRadius * 0.22, 46);
                  flash.strokeCircle(0, 0, podR + 8);
                  target.add(flash);
                  this.tweens.add({
                    targets: flash,
                    alpha: { from: 1, to: 0 },
                    scale: { from: 1, to: 1.7 },
                    duration: 800,
                    repeat: 2,
                    onComplete: () => flash.destroy(),
                  });

                  // Sparkle burst at winner's world position
                  const wp = target.getWorldTransformMatrix();
                  this._spawnSparkles(wp.tx, wp.ty);

                  chime(true);
                  resolve();
                },
              });
            },
          });
        },
      });
    });
  }

  resetForNewRound() {
    this.lastTickedSecond = null;
    this.lastTopIndex = null;
    this._lastTopFlashAt = 0;
    // Hide the tracking glow (the previous round's winner had it pinned).
    if (this._trackingGlowPulse) { this._trackingGlowPulse.stop(); this._trackingGlowPulse = null; }
    if (this._trackingGlow) {
      this.tweens.killTweensOf(this._trackingGlow);
      this._trackingGlow.setAlpha(0);
      this._trackingGlow.setScale(1);
    }
    this._clearPodLightning();
    this._stopSpotlight();
    this._unlockSpotlight();
  }

  // ---------- STATE → VIEW ----------
  onStateChange(state) {
    if (state.config && this.gondolas.length === 0) {
      this.rebuildGondolasFromState();
    }

    if (state.status === 'betting' && state.remainingMs > 0) {
      // Display real seconds remaining. Use ceil so the digit changes at the
      // exact second boundary (e.g. "1" → "0") instead of lingering for ~1s.
      // Below 5 s, render a one-decimal countdown so users perceive continuous
      // motion in the final seconds (matches realtime feel of the server).
      const ms = Math.max(0, state.remainingMs);
      let label;
      let secForPulse;
      if (ms <= 5000 && ms > 0) {
        const tenths = Math.ceil(ms / 100) / 10; // 4.9, 4.8, …
        label = tenths.toFixed(1);
        secForPulse = Math.ceil(ms / 1000);
      } else {
        secForPulse = Math.ceil(ms / 1000);
        label = `${secForPulse}`;
      }
      this.countdownText.setText(label);
      this.statusText.setText(ms > 250 ? 'BET NOW' : 'LOCKING');
      this.pulseCountdown(secForPulse);
    } else if (state.status === 'spinning') {
      this.countdownText.setText('🎡');
      this.statusText.setText('SPINNING');
    } else if (state.status === 'result') {
      this.countdownText.setText('🏆');
      this.statusText.setText('RESULT');
    } else {
      this.countdownText.setText('—');
      this.statusText.setText('WAITING');
    }

    // Visually communicate "no betting now" by dimming the gondolas during
    // spin/result. This is the right UX even though the placeBet() guard
    // now also blocks taps + beacons \u2014 users should see why their tap was
    // rejected before they even tap. We don't disableInteractive() here so
    // the tap path still fires (and beacons), giving us full diagnostics if
    // a user manages to tap a dimmed pod.
    if (this.gondolas && this.gondolas.length) {
      const bettingNow = state.status === 'betting';
      const targetAlpha = bettingNow ? 1 : (state.status === 'spinning' ? 0.82 : 0.5);
      for (const g of this.gondolas) {
        if (g.alpha !== targetAlpha) g.setAlpha(targetAlpha);
      }
    }

    this.updateBadges(state.myBets || {});
  }

  shutdown() {
    if (this.unsubscribe) this.unsubscribe();
    if (this._resizeObs) { try { this._resizeObs.disconnect(); } catch (e) {} this._resizeObs = null; }
    this._stopSpotlight();
    whooshStop();
  }
}

function formatShort(n) {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
}
